import { useEffect } from "react";
import { SectionTag, Hairline, MaskedLines } from "@/components/Reveal";
import { BRAND } from "@/data/content";

export const PageShell = ({ tag, headline, metaTitle, wide = false, children }) => {
  useEffect(() => {
    document.title = `${metaTitle} | Logibyte Technologies`;
    window.scrollTo(0, 0);
    return () => {
      document.title = "Logibyte Technologies | Building Businesses. Creating Value.";
    };
  }, [metaTitle]);

  return (
    <main
      data-testid={`page-${tag.toLowerCase().replace(/\s+/g, "-")}`}
      className="min-h-screen bg-[#0A0906] pb-28 pt-40 [color-scheme:dark]"
    >
      <div className={`mx-auto px-6 lg:px-10 ${wide ? "max-w-5xl" : "max-w-3xl"}`}>
        <SectionTag>{tag}</SectionTag>
        <h1 className="mt-6 font-serif text-3xl font-normal leading-[1.15] tracking-tight text-slate-100 sm:text-4xl lg:text-5xl">
          <MaskedLines delay={0.1}>{headline}</MaskedLines>
        </h1>
        <Hairline className="mt-10" />
        <div className="mt-12">{children}</div>
      </div>
    </main>
  );
};

const bodyCls = "space-y-6 text-base font-normal leading-relaxed text-slate-300 sm:text-lg";

const APPROACH_STAGES = [
  {
    num: "01",
    label: "IDENTIFY",
    copy: "Every Logibyte business begins with a real customer need. We study how customers currently discover and access a service, where the experience is fragmented, and whether a structured model can create a meaningfully better outcome.",
  },
  {
    num: "02",
    label: "BUILD",
    copy: "We then create the business around that need: the digital customer journey, the platform, the CRM and workflow systems, the commercial processes and the service-delivery network required to launch.",
  },
  {
    num: "03",
    label: "OPERATE",
    copy: "Once live, we run the business day to day — managing digital customer acquisition, enquiries, fulfilment, payments, support and the service professionals behind the delivery.",
  },
  {
    num: "04",
    label: "SCALE",
    copy: "Operating data and customer feedback shape every next step. We refine workflows, strengthen quality, expand reach and build more scalable versions of the model over time.",
  },
];

export const ApproachPage = () => (
  <PageShell
    tag="Our Approach"
    headline="Identify. Build. Operate. Scale."
    metaTitle="Approach"
    wide
  >
    <div className={bodyCls}>
      <p className="max-w-3xl">
        Logibyte creates businesses by connecting six things into one
        operating model: demand, technology, operations, service delivery,
        customer experience and data. The four stages below describe how that
        happens in practice.
      </p>
    </div>
    <div data-testid="approach-page-pillars" className="mt-14 border-t border-white/10">
      {APPROACH_STAGES.map((p) => (
        <div
          key={p.num}
          data-testid={`approach-page-pillar-${p.label.toLowerCase()}`}
          className="grid gap-4 border-b border-white/10 py-10 sm:grid-cols-12"
        >
          <div className="flex items-baseline gap-6 sm:col-span-4">
            <span className="text-xs font-medium tracking-[0.25em] text-[#E7CE8B]">
              {p.num}
            </span>
            <h2 className="font-serif text-2xl font-normal tracking-tight text-slate-100 sm:text-3xl">
              {p.label}
            </h2>
          </div>
          <p className="max-w-2xl text-base font-normal leading-relaxed text-slate-300 sm:col-span-8">
            {p.copy}
          </p>
        </div>
      ))}
    </div>
    <div className="mt-14 border border-white/10 bg-white/[0.02] p-8 sm:p-12">
      <p className="text-xs font-medium uppercase tracking-[0.3em] text-[#D4AF37]/80">
        How the model connects
      </p>
      <p className="mt-5 max-w-3xl text-base font-normal leading-relaxed text-slate-300 sm:text-lg">
        Customer demand enters through digital channels. Technology and CRM
        systems structure the requirement. Operations and service networks
        fulfil it. Payments and support complete the experience. Data from
        every step improves the next. That loop — repeated with discipline —
        is how a Logibyte business grows.
      </p>
    </div>
  </PageShell>
);
